import cable from "app/cable";
import { Path } from "app/path";
import { selectAccount } from "app/redux/accountSlice";
import { updateAppointment } from "app/redux/appointmentsSlice";
import { AvailabilityStatusTypes } from "app/redux/availabilitiesSlice";
import { selectActiveEmployees } from "app/redux/employeesSlice";
import {
  getSchedules,
  RecurrenceFrequency,
  selectSchedules,
} from "app/redux/schedulesSlice";
import { selectShops } from "app/redux/shopsSlice";
import {
  getWeeklySchedules,
  selectWeeklySchedules,
} from "app/redux/weeklySchedulesSlice";
import { adjustDateToUTC } from "app/utils/formatTime";
import { useQuery } from "app/utils/useQuery";
import {
  addDays,
  addHours,
  addMinutes,
  differenceInMinutes,
  format,
  subDays,
} from "date-fns";
import { toZonedTime } from "date-fns-tz";
import { setActiveShopTimeZone, toLocalDate } from "app/utils/timezone";
import { find, flattenDeep, includes, some } from "lodash";
import { useEffect, useState, useRef, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";

export interface ResourceProps {
  resourceId: number;
  resourceTitle: string;
  avatar: string;
}

export enum OtherEventTypes {
  OFF = "off",
  BLOCKED = "blocked",
}

export enum CalendarDayNavigateTypes {
  PREV = "prev",
  NEXT = "next",
  TODAY = "today",
  CUSTOM = "custom",
}

export enum CrudAppointmentActions {
  READ = "read",
  CREATE = "create",
  UPDATE = "update",
  DELETE = "delete",
  BLOCKED = "blocked",
}

export interface PendingAppointmentProps {
  employeeId: number;
  startTime: string;
  endTime: string;
  action: CrudAppointmentActions;
}

interface AppointmentModalsState {
  makeRepeatAppointment: {
    open: boolean;
    appointmentGroup: any;
    onSave?: (
      frequency: RecurrenceFrequency,
      interval: number
    ) => Promise<void>;
  };
  stopRepeatAppointment: {
    open: boolean;
    appointmentGroup: any;
    onSave?: () => Promise<void>;
  };
  editRepeatAppointment: {
    open: boolean;
    selectedClient: any;
    selectedServices: any[];
    parsedDate: Date;
    parsedTime: Date;
    onUpdateThisAppointmentOnly?: () => Promise<void>;
    onUpdateAllFutureAppointments?: () => Promise<void>;
  };
}


interface BlockTimeModalsState {
  makeRepeatBlockTime: {
    open: boolean;
    blockTime: any;
    onSave?: (
      frequency: RecurrenceFrequency,
      interval: number
    ) => Promise<void>;
  };
  stopRepeatBlockTime: {
    open: boolean;
    blockTime: any;
    onSave?: () => Promise<void>;
  };
}

export const useSchedules = () => {
  const dispatch = useDispatch();
  const query = useQuery();
  const account = useSelector(selectAccount);
  const navigate = useNavigate();
  const schedules = useSelector(selectSchedules);
  const weeklySchedules = useSelector(selectWeeklySchedules);
  const shops = useSelector(selectShops);
  const [isLoading, setIsLoading] = useState(false);
  const [currentShopTime, setCurrentShopTime] = useState(new Date());
  const employees = useSelector(selectActiveEmployees);
  const selectedShopId = query.get("shopId");
  const selectedEmployeeId = query.get("employeeId")
    ? parseInt(query.get("employeeId")!)
    : null;
  const [selectedEmployee, setSelectedEmployee] = useState<any>(
    find(employees, { id: selectedEmployeeId })
  );
  const [calendarView, setCalendarView] = useState<"daily" | "weekly">(() => {
    const view =
      (query.get("calendarView") as "daily" | "weekly") || employees.length > 1
        ? "daily"
        : "weekly";
    return view;
  });
  const [selectedWeeklyEmployee, setSelectedWeeklyEmployee] = useState<any>(
    () => {
      const selectedId = query.get("employeeId")
        ? parseInt(query.get("employeeId")!)
        : null;
      return find(employees, { id: selectedId });
    }
  );
  const [weeklyAppointments, setWeeklyAppointments] = useState<any[]>([]);
  const dateParam = query.get("date");
  const [date, setDate] = useState<Date>(
    dateParam ? new Date(dateParam) : currentShopTime
  );
  const [appointments, setAppointments] = useState<any[]>([]);
  const [resourceMap, setResourceMap] = useState<ResourceProps[]>([]);
  const [pendingAppointment, setPendingAppointment] = useState<any>();
  const [selectedAppointmentEvent, setSelectedAppointmentEvent] =
    useState(null);
  const [appointmentDrawer, setAppointmentDrawer] = useState({
    isOpen: false,
    type: null,
  } as { isOpen: boolean; type: CrudAppointmentActions | null });
  const [showPicker, setShowPicker] = useState(false);
  const [appointmentModalsState, setAppointmentModalsState] =
    useState<AppointmentModalsState>({
      makeRepeatAppointment: {
        open: false,
        appointmentGroup: null,
      },
      stopRepeatAppointment: {
        open: false,
        appointmentGroup: null,
      },
      editRepeatAppointment: {
        open: false,
        selectedClient: null,
        selectedServices: [],
        parsedDate: new Date(),
        parsedTime: new Date(),
      },
    });
  const [blockTimeModalState, setBlockTimeModalState] =
    useState<BlockTimeModalsState>({
      makeRepeatBlockTime: {
        open: false,
        blockTime: null,
      },
      stopRepeatBlockTime: {
        open: false,
        blockTime: null,
      },
    });
  const [selectedBlockTime, setSelectedBlockTime] = useState<any>(null);

  const onCustomNavigate = (newDate: Date) => {
    // For custom navigation, we always provide all three parameters
    onNavigate(newDate, null, CalendarDayNavigateTypes.CUSTOM);
  };

  const onNavigate = async (newDate: Date, view: any, action: any) => {
    let updatedDate: Date;

    if (calendarView === "weekly") {
      switch (action) {
        case CalendarDayNavigateTypes.TODAY:
          updatedDate = new Date();
          break;
        case CalendarDayNavigateTypes.PREV:
          updatedDate = subDays(newDate, 7);
          break;
        case CalendarDayNavigateTypes.NEXT:
          updatedDate = addDays(newDate, 7);
          break;
        case CalendarDayNavigateTypes.CUSTOM:
          updatedDate = newDate;
          break;
        default:
          return;
      }

      // For weekly view, handle navigation directly here
      if (selectedWeeklyEmployee?.id) {
        try {
          // Update URL and state first
          query.delete("date");
          query.delete("appointmentId");
          query.delete("phantomId");
          query.set("date", format(updatedDate, "yyyy-MM-dd"));
          query.set("calendarView", calendarView);
          navigate(`${Path.HOME}?${query.toString()}`);
        } catch (error) {
          console.error("Error during weekly navigation:", error);
        }
      }
    } else {
      switch (action) {
        case CalendarDayNavigateTypes.TODAY:
          updatedDate = new Date();
          break;
        case CalendarDayNavigateTypes.PREV:
          updatedDate = subDays(newDate, 1);
          break;
        case CalendarDayNavigateTypes.NEXT:
          updatedDate = addDays(newDate, 1);
          break;
        case CalendarDayNavigateTypes.CUSTOM:
          updatedDate = newDate;
          break;
        default:
          return;
      }

      try {
        // Update URL and state first
        query.delete("date");
        query.delete("appointmentId");
        query.delete("phantomId");
        query.set("date", format(updatedDate, "yyyy-MM-dd"));
        query.set("calendarView", calendarView);
        navigate(`${Path.HOME}?${query.toString()}`);

        // Make the API call directly here
        // await getSchedulesOfEmployees({ currentDate: updatedDate });
      } catch (error) {
        console.error("Error during daily navigation:", error);
      }
    }
    setDate(updatedDate);
    updateCalendarData(updatedDate);
  };

  const getMappedAppointments = (employeeSchedulesArray: any[]) => {
    const mappedAppointments: any[] = [];

    employeeSchedulesArray.forEach(
      ({ employeeId, appointments, availability, blockedTimes, date }: any) => {
        // For each schedule, create the appropriate events

        // First handle availability status
        if (availability?.status !== AvailabilityStatusTypes.SCHEDULED) {
          // If not scheduled, mark the entire day as OFF
          mappedAppointments.push({
            type: OtherEventTypes.OFF,
            start: new Date(`${date}T00:00:00`),
            end: new Date(`${date}T23:59:59`),
            resourceId: [employeeId],
            pending: false,
            employee: employees.find((emp) => emp.id === employeeId),
          });
        } else {
          // Handle scheduled availability with proper time windows
          if (availability.timeStart) {
            // Add OFF time from midnight to start of availability
            mappedAppointments.push({
              type: OtherEventTypes.OFF,
              start: new Date(`${date}T00:00:00`),
              end: new Date(`${date}T${availability.timeStart}:00`),
              resourceId: [employeeId],
              pending: false,
              employee: employees.find((emp) => emp.id === employeeId),
            });
          }

          if (availability.timeEnd) {
            // Add OFF time from end of availability to midnight
            mappedAppointments.push({
              type: OtherEventTypes.OFF,
              start: new Date(`${date}T${availability.timeEnd}:00`),
              end: new Date(`${date}T23:59:59`),
              resourceId: [employeeId],
              pending: false,
              employee: employees.find((emp) => emp.id === employeeId),
            });
          }
        }

        // Then handle blocked times
        if (blockedTimes?.length > 0) {
          blockedTimes.forEach((blockTime: any) => {
            const start = toLocalDate(blockTime.startTime);
            const end = toLocalDate(blockTime.endTime);

            mappedAppointments.push({
              id: blockTime.id,
              title: blockTime.name,
              start,
              end,
              type: OtherEventTypes.BLOCKED,
              resourceId: [employeeId],
              pending: false,
              employee: employees.find((emp) => emp.id === employeeId),
              blockTimeData: blockTime,
            });
          });
        }

        // Finally handle appointments
        if (appointments?.length > 0) {
          appointments.forEach((appointment: any) => {
            const start = toLocalDate(appointment.startTime);
            const end = toLocalDate(appointment.endTime);

            mappedAppointments.push({
              services: appointment.services,
              client: appointment.client,
              title: `${appointment?.services?.[0]?.title}`,
              start,
              end,
              type: "appointment",
              appointment,
              resourceId: [employeeId],
              shop: appointment.shop,
              pending: false,
              employee: employees.find((emp) => emp.id === employeeId),
            });
          });
        }
      }
    );

    return mappedAppointments;
  };

  const getMappedResources = (employeeSchedulesArray: any) => {
    const mappedEmployeeResources: ResourceProps[] = [];
    const currentParams = new URLSearchParams(window.location.search);
    const currentShopId = currentParams.get("shopId");
    employeeSchedulesArray.forEach(
      ({
        employeeId,
        employeeName,
        availability,
        employeeAvatar,
        appointments,
        shopIds,
      }: any) => {
        if (!includes(shopIds, Number(currentShopId))) {
          return;
        }
        mappedEmployeeResources.push({
          resourceId: employeeId,
          resourceTitle: employeeName,
          avatar: employeeAvatar,
        });
      }
    );

    return mappedEmployeeResources;
  };

  const calendarInit = (newSchedules: any[], currentDate: any) => {
    if (!newSchedules || newSchedules.length === 0) {
      // Don't clear appointments here if there are no new schedules
      // This prevents the flash of empty content
      return;
    }

    if (calendarView === "daily") {
      const mappedResources = getMappedResources(newSchedules);
      setResourceMap(mappedResources);
    }

    const mappedAppointments = getMappedAppointments(newSchedules);

    if (calendarView === "weekly") {
      setWeeklyAppointments(mappedAppointments);
    } else {
      setAppointments(mappedAppointments);
    }
  };

  const getSchedulesOfEmployees = async ({
    currentDate = currentShopTime,
    specificEmployeeId,
  }: {
    currentDate?: Date;
    specificEmployeeId?: number;
  }) => {
    const currentParams = new URLSearchParams(window.location.search);
    const currentShopId = currentParams.get("shopId");

    const employeeIds = employees
      .filter((employee) =>
        some(employee.shops, (shop) => shop.id === Number(currentShopId))
      )
      .map((employee) => employee.id);

    try {
      const newSchedules = await dispatch(
        getSchedules({
          employeeIds: specificEmployeeId ? [specificEmployeeId] : employeeIds,
          date: currentDate,
        }) as any
      ).unwrap();
      calendarInit(newSchedules, currentDate);
    } catch (error) {
      console.log("Error fetching schedules:", error);
    }
  };

  const getWeeklySchedulesOfEmployees = async ({
    employeeId,
    date,
  }: {
    employeeId: number;
    date: Date;
  }) => {
    setIsLoading(true);
    navigate(`${Path.HOME}?${query.toString()}`);

    try {
      const weeklySchedules = await dispatch(
        getWeeklySchedules({
          employeeId,
          date,
        }) as any
      ).unwrap();

      // Only update the UI if we're still in weekly view
      if (calendarView === "weekly") {
        calendarInit(normalizeWeeklySchedules(weeklySchedules), date);
      }
    } catch (error) {
      console.log("Error fetching weekly schedules:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const onUpdateAppointment = async ({
    event,
    resourceId,
    start,
    end,
  }: any) => {
    if (event.pending) {
      return createNewPendingAppointment({ ...event, start, end });
    } else if (
      resourceId !== event.services?.[0]?.employee?.id ||
      event.services.length === 0
    ) {
      return;
    } else {
      const appointment = event.appointment;
      const startTime = format(start, "EEE MMM dd yyyy HH:mm:ss");
      const endTime = format(end, "EEE MMM dd yyyy HH:mm:ss");

      try {
        setIsLoading(true);
        await dispatch(
          updateAppointment({
            appointmentId: appointment.id,
            startTime,
            endTime,
            employeeId: resourceId,
          }) as any
        ).unwrap();
      } catch (error) {
        console.log("Error updating appointment:", error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const onNewAppointmentInit = ({
    startTime = new Date(date),
    endTime = addHours(new Date(date), 1),
  }: {
    startTime?: Date;
    endTime?: Date;
  }) => {
    createNewPendingAppointment({
      employee: selectedEmployee ?? selectedWeeklyEmployee,
      client: null,
      start: startTime,
      end: endTime,
      services: [],
    });

    toggleAppointmentDrawer(true, CrudAppointmentActions.CREATE);
  };

  const onNewSlotSelected = ({ start, end, resourceId, action }: any) => {
    if (action === "doubleClick") {
      return;
    }

    setAppointmentDrawer({ isOpen: false, type: null });
    const employeeId = Array.isArray(resourceId)
      ? Number(resourceId[0])
      : Number(resourceId);
    const employee = employees.find((emp) => emp.id === employeeId);
    if (!employee) {
      return;
    }

    createNewPendingAppointment({
      employee,
      services: [],
      client: null,
      start,
      end,
    });

    setAppointmentDrawer({
      isOpen: true,
      type: CrudAppointmentActions.CREATE,
    });
  };

  const onAppointmentSelect = async (event: any) => {
    if (event.type === OtherEventTypes.OFF || event.pending) {
      return;
    }

    if (event.type === OtherEventTypes.BLOCKED) {
      setSelectedBlockTime(event);
      query.set("blockTimeId", event.id);
      query.set("calendarView", calendarView);
      navigate(`${Path.HOME}?${query.toString()}`);
      return setAppointmentDrawer({
        isOpen: true,
        type: CrudAppointmentActions.BLOCKED,
      });
    }
    setSelectedAppointmentEvent(event);
    query.delete("appointmentId");
    query.delete("phantomId");
    query.set("appointmentId", event.appointment?.id);
    if (event.shop?.id) {
      query.set("shopId", event.shop?.id);
    }
    if (event.appointment?.phantomId) {
      query.set("phantomId", event.appointment?.phantomId);
    }
    query.set("calendarView", calendarView);
    navigate(`${Path.HOME}?${query.toString()}`);
    setAppointmentDrawer({ isOpen: true, type: CrudAppointmentActions.READ });
  };

  const toggleAppointmentDrawer = (
    open: boolean,
    type?: CrudAppointmentActions
  ) => {
    setAppointmentDrawer({ isOpen: open, type: type || null });
  };

  const createNewPendingAppointment = (event: any) => {
    query.delete("appointmentId");
    query.delete("phantomId");
    // Ensure calendarView param is preserved
    query.set("calendarView", calendarView);
    navigate(`${Path.HOME}?${query.toString()}`);
    const newAppointment = {
      employee: event.employee,
      services: event.services,
      client: event.client,
      title: "New Appointment",
      start: event.start,
      end:
        differenceInMinutes(event.end, event.start) < 40
          ? addMinutes(event.start, 40)
          : event.end,
      resourceId: [event.employee.id],
      pending: true,
    };
    setPendingAppointment(newAppointment);
    setSelectedBlockTime({ ...newAppointment, title: "Break" });
  };

  const openMakeRepeatAppointmentModal = (data: {
    appointmentGroup: any;
    onSave: (frequency: RecurrenceFrequency, interval: number) => Promise<void>;
  }) => {
    setAppointmentModalsState((prevState) => ({
      ...prevState,
      makeRepeatAppointment: {
        ...data,
        open: true,
      },
    }));
  };

  const openStopRepeatAppointmentModal = (data: {
    appointmentGroup: any;
    onSave: () => Promise<void>;
  }) => {
    setAppointmentModalsState((prevState) => ({
      ...prevState,
      stopRepeatAppointment: {
        ...data,
        open: true,
      },
    }));
  };

  const openEditRepeatAppointmentModal = (data: {
    selectedClient: any;
    selectedServices: any[];
    parsedDate: Date;
    parsedTime: Date;
    onUpdateThisAppointmentOnly: () => Promise<void>;
    onUpdateAllFutureAppointments: () => Promise<void>;
  }) => {
    setAppointmentModalsState((prevState) => ({
      ...prevState,
      editRepeatAppointment: {
        ...data,
        open: true,
      },
    }));
  };

  const closeMakeRepeatAppointmentModal = () => {
    setAppointmentModalsState((prevState) => ({
      ...prevState,
      makeRepeatAppointment: {
        appointmentGroup: null,
        onSave: undefined,
        open: false,
      },
    }));
  };

  const closeStopRepeatAppointmentModal = () => {
    setAppointmentModalsState((prevState) => ({
      ...prevState,
      stopRepeatAppointment: {
        appointmentGroup: null,
        onSave: undefined,
        open: false,
      },
    }));
  };

  const closeEditRepeatAppointmentModal = () => {
    setAppointmentModalsState((prevState) => ({
      ...prevState,
      editRepeatAppointment: {
        selectedClient: null,
        selectedServices: [],
        parsedDate: new Date(),
        parsedTime: new Date(),
        onUpdateThisAppointmentOnly: undefined,
        onUpdateAllFutureAppointments: undefined,
        open: false,
      },
    }));
  };

  const openMakeRepeatBlockTimeModal = (data: {
    blockTime: any;
    onSave: (frequency: RecurrenceFrequency, interval: number) => Promise<void>;
  }) => {
    setBlockTimeModalState((prevState) => ({
      ...prevState,
      makeRepeatBlockTime: {
        ...data,
        open: true,
      },
    }));
  };

  const openStopRepeatBlockTimeModal = (data: {
    blockTime: any;
    onSave: () => Promise<void>;
  }) => {
    setBlockTimeModalState((prevState) => ({
      ...prevState,
      stopRepeatBlockTime: {
        ...data,
        open: true,
      },
    }));
  };

  const closeMakeRepeatBlockTimeModal = () => {
    setBlockTimeModalState((prevState) => ({
      ...prevState,
      makeRepeatBlockTime: {
        blockTime: null,
        onSave: undefined,
        open: false,
      },
    }));
  };

  const closeStopRepeatBlockTimeModal = () => {
    setBlockTimeModalState((prevState) => ({
      ...prevState,
      stopRepeatBlockTime: {
        blockTime: null,
        onSave: undefined,
        open: false,
      },
    }));
  };

  const updateCalendarData = async (dateOverride?: Date) => {
    // dateOverride is used to update the calendar data for a specific date
    const dateToUse = dateOverride || date;
    try {
      setIsLoading(true);
      if (calendarView === "weekly") {
        setSelectedEmployee(null);

        if (selectedWeeklyEmployee?.id && dateToUse) {
          query.set("employeeId", selectedWeeklyEmployee.id.toString());
          query.set("calendarView", "weekly");
          navigate(`${Path.HOME}?${query.toString()}`);
          await getWeeklySchedulesOfEmployees({
            employeeId: selectedWeeklyEmployee.id,
            date: dateToUse,
          });
        }
        return;
      }

      if (!selectedEmployee && employees.length > 0) {
        setSelectedEmployee(employees[0]);
      }

      await getSchedulesOfEmployees({
        currentDate: dateToUse,
      });
    } catch (err) {
      console.log("Error updating calendar data:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const normalizeWeeklySchedules = (weeklySchedules: any) => {
    const normalizedSchedules: any[] = [];
    if (!weeklySchedules || !weeklySchedules.data) {
      return normalizedSchedules;
    }

    // Each schedule in data represents a day's schedule
    weeklySchedules.data.forEach((daySchedule: any) => {
      normalizedSchedules.push({
        employeeId: Number(daySchedule.employeeId),
        employeeName: daySchedule.employeeName,
        employeeAvatar: daySchedule.employeeAvatar,
        shopIds: daySchedule.shopIds || [],
        date: daySchedule.start?.split("T")[0] || daySchedule.date,
        availability: daySchedule.availability || {
          timeStart: "09:00",
          timeEnd: "17:00",
          status: AvailabilityStatusTypes.SCHEDULED,
        },
        appointments: daySchedule.appointments || [],
        blockedTimes: daySchedule.blockedTimes || [],
      });
    });

    return normalizedSchedules;
  };

  useEffect(() => {
    if (query.get("appointmentId")) {
      const appointmentId = parseInt(query.get("appointmentId") as string);
      const appointment = find(
        flattenDeep(appointments),
        (appointment) => appointment.appointment?.id === appointmentId
      );
      if (appointment) {
        setSelectedAppointmentEvent(appointment);
        setAppointmentDrawer({
          isOpen: true,
          type: CrudAppointmentActions.READ,
        });
      }
    } else if (query.get("blockTimeId")) {
      const blockTimeId = parseInt(query.get("blockTimeId") as string);
      const blockTime = find(
        flattenDeep(appointments),
        (appointment) => appointment.id === blockTimeId
      );
      if (blockTime) {
        setSelectedBlockTime(blockTime);
        setAppointmentDrawer({
          isOpen: true,
          type: CrudAppointmentActions.BLOCKED,
        });
      }
    }
  }, [appointments]);

  useEffect(() => {
    const currentViewDate = query.get("date");

    if (currentViewDate) {
      updateCalendarData(new Date(currentViewDate));
    }
  }, [calendarView, selectedWeeklyEmployee]);

  useEffect(() => {
    let date = query.get("date");
    let shopId = query.get("shopId");
    let employeeId = query.get("employeeId");
    let calendarView = query.get("calendarView");

    if (!shopId || !date || !employeeId) {
      if (!shopId) {
        shopId = shops[0]?.id.toString();
        query.set("shopId", shopId);
      }
      if (!date) {
        date = format(new Date(), "yyyy-MM-dd");
        query.set("date", format(new Date(), "yyyy-MM-dd"));
      }
      if (!employeeId && calendarView === "weekly") {
        employeeId = selectedWeeklyEmployee?.id.toString();
        query.set("employeeId", selectedWeeklyEmployee?.id.toString());
      }
      if (!calendarView) {
        calendarView = "daily";
        query.set("calendarView", calendarView);
      }
      navigate(`${Path.HOME}?${query.toString()}`);
    }

    const adjustedDate = adjustDateToUTC(date);
    setDate(adjustedDate);
    setCurrentShopTime(
      toZonedTime(
        new Date(),
        find(shops, { id: Number(shopId) })?.timezone.timeZoneId
      )
    );
    updateCalendarData(adjustedDate);
  }, [selectedShopId]);

  useEffect(() => {
    setActiveShopTimeZone(
      find(shops, { id: Number(selectedShopId) })?.timezone?.timeZoneId
    );
  }, [selectedShopId, shops]);

  useEffect(() => {
    const subscription = cable.subscriptions.create(
      {
        channel: "SchedulesChannel",
        account_id: account.id,
      },
      {
        connected: () => {},
        disconnected: () => {},
        received: async (data) => {
          await updateCalendarData(adjustDateToUTC(data.date));
          // if (calendarView === "daily") {
          //   await getSchedulesOfEmployees({
          //     currentDate: adjustDateToUTC(data.date),
          //   });
          // } else {
          //   console.log("weekly --- data", data);
          //   await getWeeklySchedulesOfEmployees({
          //     employeeId: selectedWeeklyEmployee?.id,
          //     date: adjustDateToUTC(data.date),
          //   });
          // }
        },
      }
    );

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  // Save calendar view preference to URL query parameter whenever it changes
  const handleCalendarViewChange = (view: "daily" | "weekly") => {
    // Update URL parameter
    query.set("calendarView", view);
    navigate(`${Path.HOME}?${query.toString()}`);

    // Update state
    setCalendarView(view);
  };

  return {
    getSchedulesOfEmployees,
    getWeeklySchedulesOfEmployees,
    onNavigate,
    date,
    schedules,
    appointments,
    resourceMap,
    onUpdateAppointment,
    onNewSlotSelected,
    appointmentDrawer,
    setAppointmentDrawer,
    onAppointmentSelect,
    selectedAppointmentEvent,
    setSelectedAppointmentEvent,
    toggleAppointmentDrawer,
    pendingAppointment,
    setPendingAppointment,
    currentShopTime,
    selectedEmployee,
    setSelectedEmployee,
    createNewPendingAppointment,
    onNewAppointmentInit,
    openMakeRepeatAppointmentModal,
    openStopRepeatAppointmentModal,
    openEditRepeatAppointmentModal,
    closeMakeRepeatAppointmentModal,
    closeStopRepeatAppointmentModal,
    closeEditRepeatAppointmentModal,
    appointmentModalsState,
    selectedBlockTime,
    setSelectedBlockTime,
    blockTimeModalState,
    openMakeRepeatBlockTimeModal,
    openStopRepeatBlockTimeModal,
    closeMakeRepeatBlockTimeModal,
    closeStopRepeatBlockTimeModal,
    showPicker,
    setShowPicker,
    onCustomNavigate,
    employees,
    calendarView,
    setCalendarView: handleCalendarViewChange,
    selectedWeeklyEmployee,
    setSelectedWeeklyEmployee,
    weeklySchedules,
    weeklyAppointments,
    isLoading,
  };
};
